/**
 * CloseButton — Standardized close/dismiss button
 *
 * Replaces inconsistent ×, &times;, custom SVG, and emoji close buttons
 * throughout the application with a single, theme-aware, accessible component.
 *
 * Variants:
 *   - default:  Subtle dismiss (modals, panels, drawers)
 *   - danger:   Destructive action hover (remove items, delete)
 *   - overlay:  Glass-style for lightboxes and media overlays
 *
 * Sizes: sm (24px), md (32px), lg (40px)
 *
 * Follows CLAUDE.md conventions:
 *   - useAppTranslation for i18n
 *   - CSS custom property tokens only
 *   - CSS logical properties for RTL
 */

import React from 'react';
import { X } from 'lucide-react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import styles from './CloseButton.module.css';

type CloseButtonSize = 'sm' | 'md' | 'lg';
type CloseButtonVariant = 'default' | 'danger' | 'overlay';

interface CloseButtonProps {
  /** Click handler */
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** Visual size. Default: 'md' */
  size?: CloseButtonSize;
  /** Style variant. Default: 'default' */
  variant?: CloseButtonVariant;
  /** Override the default aria-label. Localized "Close" is used if omitted. */
  ariaLabel?: string;
  /** Additional CSS class */
  className?: string;
  /** Optional title tooltip */
  title?: string;
  /** Tab index override (-1 to skip tab order) */
  tabIndex?: number;
}

const ICON_SIZE: Record<CloseButtonSize, number> = {
  sm: 14,
  md: 18,
  lg: 22,
};

const STROKE_WIDTH: Record<CloseButtonSize, number> = {
  sm: 2.2,
  md: 2,
  lg: 1.8,
};

const CloseButton: React.FC<CloseButtonProps> = ({
  onClick,
  size = 'md',
  variant = 'default',
  ariaLabel,
  className,
  title,
  tabIndex,
}) => {
  const { t } = useAppTranslation('common');

  const classes = [
    styles.closeBtn,
    styles[size],
    variant !== 'default' ? styles[variant] : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      aria-label={ariaLabel || t('button.close', { defaultValue: 'Close' })}
      title={title}
      tabIndex={tabIndex}
    >
      <X size={ICON_SIZE[size]} strokeWidth={STROKE_WIDTH[size]} />
    </button>
  );
};

export default CloseButton;
